import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Fade,
  LinearProgress,
  Divider,
  Card,
  CardContent,
  Avatar,
  Stack,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  LockOutlined as LockIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { LogoImage } from "../components/layout/Logo";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, error: authError, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync with auth context errors
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  // Clear error when input changes
  useEffect(() => {
    if (error) {
      setError(null);
    }
  }, [email, password]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  // Form validation
  const validateForm = (): boolean => {
    let isValid = true;
    let formError = null;

    // Reset error state
    setError(null);

    // Basic validation
    if (!email.trim()) {
      formError = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      formError = "Please enter a valid email address";
      isValid = false;
    } else if (!password) {
      formError = "Password is required";
      isValid = false;
    }

    if (formError) {
      setError(formError);
    }

    return isValid;
  };

  // Use combined loading state
  const isLoading = loading || authLoading;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      setError(null);
      setLoading(true);
      await login(email, password);
      navigate("/dashboard");
    } catch (err: any) {
      // Check if user is not confirmed
      if (err.message && err.message.includes("User is not confirmed")) {
        // Redirect to confirmation page with email
        navigate("/confirm-account", {
          state: {
            email: email,
            message:
              "Your account needs to be confirmed. Please check your email for the confirmation code.",
          },
        });
        return;
      }
      // Error will be set via the useEffect that watches authError
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card sx={{ borderRadius: 2, overflow: "visible" }}>
        {isLoading && <LinearProgress sx={{ borderRadius: "4px 4px 0 0" }} />}
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            <LogoImage width={120} height={120} noText={false} />
            <Typography variant="h4" component="h1" gutterBottom>
              Sign In
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Sign in to access your CanYa activities
            </Typography>
          </Box>

          {/* Error message with fade effect - using div wrapper so Fade always has a child */}
          <Fade in={error !== null}>
            <div style={{ width: "100%", marginBottom: error ? "16px" : 0 }}>
              {error && (
                <Alert
                  severity="error"
                  variant="filled"
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}
            </div>
          </Fade>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              error={!!error}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              error={!!error}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.2 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign In"
              )}
            </Button>

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                mb: 1,
              }}
            >
              <Link
                component={RouterLink}
                to="/forgot-password"
                variant="body2"
                underline="hover"
                color="primary"
              >
                Forgot password?
              </Link>
              <Link
                component={RouterLink}
                to="/register"
                variant="body1"
                underline="hover"
              >
                Don't have an account? Sign Up
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Login;
